import { useState, useRef, useEffect } from 'react';
import {
  Table,Avatar
} from "flowbite-react";
import { useProjectService, useRoadmapService } from '../../../services';
import { useSelector } from 'react-redux';
import { HiChevronUpDown } from "react-icons/hi2";
import { HiDocumentText } from "react-icons/hi";
import { exportToExcel } from 'react-json-to-excel';
import { getTimeZoneDate,formatDate } from "../../../helpers/data-format";
import { HiMagnifyingGlass } from "react-icons/hi2";
import { getSessionStorageItem, setSessionStorageItem, saveSearchState } from '../../../utils/sessionStorageUtils';
import { SearchInput, SingleSelect } from '../../../components/common/Search';
import { HiAdjustmentsHorizontal } from "react-icons/hi2";
import ColumnVisibility from '../../../components/common/ColumnVisibility';

// Add helper functions for column visibility storage
const getColumnVisibility = (projectId: string) => {
  const stored = localStorage.getItem(`columnVisibility_${projectId}`);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {
      return null;
    }
  }
  return null;
};

const saveColumnVisibility = (projectId: string, visibility: Record<string, boolean>) => {
  localStorage.setItem(`columnVisibility_${projectId}`, JSON.stringify(visibility));
};

const tableHeadMap = [
  {
    key:"item_id",
    label:"Id"
  },
  {
    key:"task_type",
    label:"Type"
  },
  {
    key:"task_title",
    label:"Title"
  },
  {
    key:"dependency",
    label:"Block By"
  },
  {
    key:"duration",
    label:"Duration"
  },
  {
    key:"member",
    label:"Members"
  },
  {
    key:"skill",
    label:"Skill"
  },
  {
    key:"start",
    label:"Start Date"
  },
  {
    key:"completion",
    label:"Completion"
  }];

interface TaskTableProps {
  project?:any,
  onClick?:any
  addNewTask?:any
  setAddNewTask?:any
  handleAddNewTask?:any
  handleEditAddNewTask?: (task: any) => void
  handleViewTask?:any
  projectId?:any
  sim_flag?:any
  isToken?:any
  token?:any
}

const TaskTable = (
    { projectId,
      sim_flag,
      addNewTask,
      handleEditAddNewTask,
      handleViewTask,
      isToken,
      token='',
      ...props 
    }:TaskTableProps) => {
  const [sortObj, setSortObj] = useState({});
  const [filterTaskList, setFilterTaskList] = useState<any[]>([]);
  const [downloadList, setDownloadList] = useState([]);
  const [localLoading, setLocalLoading] = useState(true);
  const { taskList, taskListLoading, data, hasSimulationTasks } = useSelector((state: any) => state.projects)
  
  // Read search state from session storage based on project ID
  const [searchTerm, setSearchTerm] = useState(() => {
    const v = getSessionStorageItem(projectId, 'searchTerm', '');
    return typeof v === 'string' ? v : (v == null ? '' : String(v));
  });
  
  const [searchField, setSearchField] = useState(() => 
    getSessionStorageItem(projectId, 'searchField', 'all')
  );
  
  // When search state changes, update session storage too
  const updateSearchTerm = (value: string) => {
    setSessionStorageItem(projectId, 'searchTerm', value);
    setSearchTerm(value);
  };
  
  const updateSearchField = (value: string) => {
    setSessionStorageItem(projectId, 'searchField', value);
    setSearchField(value);
  };

  const {fetchTaskData, checkSimulationTasks} = useProjectService();
  const { fetchProjectShareData } = useRoadmapService();

  const searchOptions = [
    { value: "all", label: "All Fields" },
    { value: "item_id", label: "ID" },
    { value: "task_title", label: "Title" },
    { value: "task_type", label: "Type" },
    { value: "skill", label: "Skill" },
    { value: "member", label: "Member" },
    { value: "completion", label: "Completion %" }
  ];

  // Simplify the useEffect hooks to reduce duplicate logic
  useEffect(() => {
    // Always show loader when data source changes
    setLocalLoading(true);
    
    // Reset loading state when sim_flag or projectId changes
  }, [sim_flag, projectId]);

  useEffect(() => {
    // Only fetch data if we have a project ID
    if (projectId && !isToken) {
      // This call now intelligently handles both fetching the current mode data
      // and checking for simulation tasks only if needed (when live mode has no tasks)
      fetchTaskData(projectId, sim_flag);
    } else if(token && isToken){
      fetchProjectShareData(token,sim_flag,{});
    }
  },[projectId, sim_flag, token, isToken]);

  // Apply search filters to the task list data
  const applyCurrentFilters = (tasks: any[]) => {
    if (!searchTerm) {
      return tasks;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return tasks.filter((task: any) => {
      if (searchField === "completion") {
        const completionValue = Number(task.completion || 0);
        
        if (searchTerm.includes("-")) {
          // Handle range search (e.g., "20-50")
          const [min, max] = searchTerm.split("-").map(Number);
          return completionValue >= min && completionValue <= max;
        } else {
          // Handle exact number search
          return completionValue === Number(searchTerm);
        }
      }

      if (searchField === "all") {
        return (
          String(task.item_id ?? '').toLowerCase().includes(searchLower) ||
          String(task.task_title ?? '').toLowerCase().includes(searchLower) ||
          String(task.task_type ?? '').toLowerCase().includes(searchLower) ||
          String(task.skill ?? '').toLowerCase().includes(searchLower) ||
          task.member?.name?.toLowerCase().includes(searchLower) ||
          task.completion?.toString() === searchTerm // Exact match for completion
        );
      }


      
      if (searchField === "member") {
        return task.member?.name?.toLowerCase().includes(searchLower);
      }

      const value = task?.[searchField as keyof typeof task];
      if (value == null) return false;
      return String(value).toLowerCase().includes(searchLower);
    });
  };

  // Handle user search input
  const handleSearch = (searchValue: string) => {
    // Use the session storage updater
    updateSearchTerm(searchValue);
  };

  const getPlaceholder = () => {
    if (searchField === "completion") {
      return "Enter % or range (e.g., 20-50)";
    }
    return `Search by ${searchOptions.find(opt => opt.value === searchField)?.label.toLowerCase()}...`;
  };

  const members: any[] = []

  if(taskList && taskList.length){
    taskList.forEach((task:any) => { 
      if(task?.member?.name) {
        members.push(
          {
            value:task?.member?.id,
            label:task?.member?.name
        })
      }
    })
  }

  // const handleSelectChange = (value:any) =>{
  //   setSelectedMember(value)
  // }
  const handleSortClick = (key: string) => {
    if(key === "task_id") return 
    const obj: Record<string, boolean> = {...sortObj}
    const filterTaskListArr = [...filterTaskList].sort((a: any, b: any) => {
      if(key === "member"){
        const d1 = a[key]?.name ?? "";
        const d2 = b[key]?.name ?? "";
        if(obj[key]) {
          if(d1 < d2) return -1;
          if(d1 > d2) return 1;
        } else {
          if(d1 > d2) return -1;
          if(d1 < d2) return 1;
        }
      } else if(key === "start"){
        const d1 = a[key] === null ? "" : new Date(a[key]);
        const d2 = b[key] === null ? "" : new Date(b[key]);
        if(obj[key]) {
          if(d1 < d2) return -1;
          if(d1 > d2) return 1;
        } else {
          if(d1 > d2) return -1;
          if(d1 < d2) return 1;
        }
      } else if(key === "item_id"){
        // Extract numeric parts for natural sorting of alphanumeric values
        const getNumericPart = (str: string) => {
          if (!str) return 0;
          const matches = str.match(/\d+/g);
          return matches ? parseInt(matches[matches.length - 1], 10) : 0;
        };
        
        // Get the numeric parts from both IDs
        const numA = getNumericPart(a[key] || "");
        const numB = getNumericPart(b[key] || "");
        
        // If numeric parts are different, sort by numeric value
        if (numA !== numB) {
          return obj[key] ? numA - numB : numB - numA;
        } 
        
        // If numeric parts are the same, fall back to string comparison
        const strA = a[key] || "";
        const strB = b[key] || "";
        
        if(obj[key]) {
          return strA.localeCompare(strB);
        } else {
          return strB.localeCompare(strA);
        }
      } else {
        const d1 = a[key] ?? "";
        const d2 = b[key] ?? "";
        if(obj[key]) {
          if(d1 < d2) return -1;
          if(d1 > d2) return 1;
        } else {
          if(d1 > d2) return -1;
          if(d1 < d2) return 1;
        }
      }
      return 0;
    });
    obj[key] = !obj[key]
    setSortObj(obj);
    setFilterTaskList(filterTaskListArr)
  }

  const handleDownloadFile = () => {
    const exelData:any = [...taskList].map((ele) => {
      const mappeddata = getMapObj(ele);
      return {
        ...mappeddata,
      }
    });
    const listArrr = [{
      sheetName:`${data.project_name}_${+new Date()}`.substring(30),
      details:[...exelData]
    }]
    exportToExcel(listArrr, `${data.project_name}_${+new Date()}`,true)
  }

  const getMapObj = (ele:any) => {
    return {
      "Task ID" : ele.item_id,
      "Task Name" : ele.task_title,
      "Task Type" : ele.task_type,
      "Task Description" : ele.description,
      "Dependent On" : ele?.dependency?.join(",") || "",
      "Duration (In Days)" : ele.duration,
      "Member" : ele?.member?.name || "",
      "Skill" : ele?.skill,
      "Start Date" : ele?.adj_start_date,
      "Completion" : ele?.completion,
    }
  }

  // Save search state on component unmount
  useEffect(() => {
    return () => {
      saveSearchState(projectId, searchTerm, searchField);
    };
  }, [searchTerm, searchField, projectId]);
  
  // Handle loading state and data processing
  useEffect(() => {
    // If the task list has loaded, process it
    if (!taskListLoading && taskList) {
      // Apply filter with existing search terms
      const filteredTasks = taskList.length ? applyCurrentFilters(taskList) : [];
      setFilterTaskList(filteredTasks);
      
      // Update download list data
      if (taskList.length) {
        const downloadListArr:any = [...taskList].map(task => {
          if (task?.member?.name) {
            return {...task, member:task?.member?.name}
          } else {
            return {...task, member:''}
          }
        });
        setDownloadList(downloadListArr);
      }
      
      // Set a minimum display time for the loader to prevent flickering
      // and ensure a consistent user experience even if data loads quickly
      setTimeout(() => {
        setLocalLoading(false);
      }, 1000); 
    }
  }, [taskList, taskListLoading, searchTerm, searchField]);

  // Initialize visibleColumns from localStorage if available
  const [visibleColumns, setVisibleColumns] = useState(() => {
    if (projectId) {
      const stored = getColumnVisibility(projectId);
      if (stored) {
        return stored;
      }
    }
    // Default all columns visible if no stored state
    const obj: Record<string, boolean> = {};
    tableHeadMap.forEach(col => {
      obj[col.key] = true;
    });
    return obj;
  });
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const columnDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (columnDropdownRef.current && !columnDropdownRef.current.contains(event.target as Node)) {
        setShowColumnDropdown(false);
      }
    }
    if (showColumnDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showColumnDropdown]);

  // Save column visibility to localStorage when it changes
  useEffect(() => {
    if (projectId) {
      saveColumnVisibility(projectId, visibleColumns);
    }
  }, [visibleColumns, projectId]);

  const handleColumnToggle = (key: string) => {
    const visibleCount = Object.values(visibleColumns).filter(Boolean).length;
    
    if (visibleCount === 1 && visibleColumns[key]) {
      return;
    }
    
    setVisibleColumns((prev: Record<string, boolean>) => {
      const newState = { ...prev, [key]: !prev[key] };
      return newState;
    });
  };

  return (
    <div className="row dark:bg-[#111928] w-full">
      
      <div className="sticky top-0 z-50 bg-gray-50 dark:bg-[#111928] border-b dark:border-gray-700 py-2">
        <div className="flex justify-end items-center gap-4 px-4">
          <div className="flex items-center gap-2">
            <SingleSelect
              name="searchType"
              options={searchOptions}
              placeholder="Select search type..."
              onChange={(selectedOption) => {
                    // When changing search field type, always clear the search term
                const newSearchField = selectedOption.value;
                
                    // Update the search field first
                updateSearchField(newSearchField);

                    // Always clear the search term when changing field type
                updateSearchTerm("");
              }}
              defaultValue={searchOptions.find(option => option.value === searchField)}
            />
            <SearchInput
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={getPlaceholder()}
            />
            <button 
              onClick={handleDownloadFile}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg border border-gray-500 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-100 transition-all duration-200 h-[42px] focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-500 dark:focus:border-blue-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <HiDocumentText className="w-5 h-5" />
              <span className="font-['Proxima Nova']">Download Excel</span>
            </button>
          </div>
        </div>
      </div>
      
      <section className="bg-gray-50 dark:bg-[#111928] py-3 sm:py-5">
          <div className="bg-white dark:bg-[#111928] relative overflow-hidden flex flex-col">
            <div className="overflow-x-auto table-wrp block max-h-[75vh]">
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <Table className="min-w-full relative divide-y divide-gray-200 dark:divide-gray-600 w-full text-sm text-left text-gray-500 dark:text-gray-400 bg-white mb-0 dark:border-none dark:bg-slate-800 rounded-lg overflow-hidden">
                    <Table.Head className="border-b sticky top-0 bg-gray-100 dark:bg-gray-700 z-[99] dark:text-white font-bold text-xs relative">
                      {tableHeadMap.filter(head => visibleColumns[head.key]).map(head => {
                        return (
                          <Table.HeadCell 
                            className="!px-6 !py-3 cursor-pointer relative" 
                            key={head.key}
                          >
                            <div className="flex " onClick={()=>handleSortClick(head.key)}>
                              <span>{head.label}</span>
                              {head.key !== "task_id" && <div className="absolute right-0 top-[30%]"> <HiChevronUpDown className="text-xl ml-2" /></div>}
                            </div>
                          </Table.HeadCell>)
                      })}
                      <Table.HeadCell className="!px-6 !py-3 w-16 text-center relative">
                        <ColumnVisibility
                          columns={tableHeadMap}
                          visibleColumns={visibleColumns}
                          onColumnToggle={handleColumnToggle}
                          projectId={projectId}
                        />
                      </Table.HeadCell>
                    </Table.Head>
                    <Table.Body className="max-h-[75vh] overflow-y-auto divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                      {taskListLoading || localLoading ? (
                        <Table.Row>
                          <Table.Cell colSpan={tableHeadMap.filter(head => visibleColumns[head.key]).length} className="text-center py-10">
                            <div className="flex items-center flex-col justify-center w-full h-[50vh] bottom-0 relative">
                              <img alt="cadence Loader" className="w-40 mb-1" src="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/loader.gif" />
                              <div className="text-center dark:text-white text-gray-700 text-base font-normal">Cadence is working<br />on your request</div>
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      ) : filterTaskList && filterTaskList.length > 0 ? (
                        filterTaskList.map((task:any, index:number) => { 
                          return (
                            <Table.Row 
                                className={`hover:bg-gray-100  dark:hover:bg-gray-700 dark:text-white cursor-pointer font-medium `} 
                                key={task.item_id} 
                                onDoubleClick={() => handleEditAddNewTask && handleEditAddNewTask(task)}>
                                  {tableHeadMap.filter(head => visibleColumns[head.key]).map(head => {
                                    switch (head.key) {
                                      case "item_id":
                                        return (
                                          <Table.Cell className="!px-6 !py-3 relative" key={head.key}>
                                            {task.item_id}
                                            <div className={`absolute bg-green-700 ${task.isDeleted=== true ? "!bg-red-400" : "bg-green-700 hidden"} text-white left-0 text-[10px] translate-y-[-25px] translate-x-[-12px] w-10 flex justify-center rotate-[-45deg] p-0.5 z-[1] rounded-sm`}> Del </div>
                                            <div className={`absolute bg-green-700 ${task.sim_flag=== true ? "!bg-green-700" : "bg-green-700 hidden"} text-white left-0 text-[10px] translate-y-[-25px] translate-x-[-12px] w-10 flex justify-center rotate-[-45deg] p-0.5 z-[1] rounded-sm`}> Add </div>
                                            <div className={`absolute bg-green-700 ${task.isUpdated=== true ? "!bg-yellow-400" : "bg-green-700 hidden"} text-white left-0 text-[10px] translate-y-[-25px] translate-x-[-12px] w-10 flex justify-center rotate-[-45deg] p-0.5 z-[1] rounded-sm`}> Sim </div>
                                          </Table.Cell>
                                        );
                                      case "task_type":
                                        return (
                                          <Table.Cell className="!px-6 !py-3" key={head.key}>
                                            {task.task_type}
                                          </Table.Cell>
                                        );
                                      case "task_title":
                                        return (
                                          <Table.Cell title={task.task_title} className="!px-6 !py-3 max-w-[200px] truncate" key={head.key}>
                                            {task.task_title}
                                          </Table.Cell>
                                        );
                                      case "dependency":
                                        return (
                                          <Table.Cell className="!px-6 !py-3" key={head.key}>
                                            {Array.isArray(task?.dependency)
                                              ? task.dependency.join(",")
                                              : (task?.dependency != null ? String(task.dependency) : "")}
                                          </Table.Cell>
                                        );
                                      case "duration":
                                        return (
                                          <Table.Cell className="!px-6 !py-3" key={head.key}>
                                            {task.duration}
                                          </Table.Cell>
                                        );
                                      case "member":
                                        return (
                                          <Table.Cell className="!px-6 !py-3 flex items-center gap-2 text-center" key={head.key}>
                                            {task?.member?.name && <Avatar
                                              placeholderInitials={task?.member?.name?.slice(0,1)}
                                              stacked
                                              alt={task?.member?.name}
                                              title={task?.member?.name}
                                              rounded
                                              className="!rounded-full"
                                              img={task?.member?.image_url}
                                            />}
                                            <span className="first-letter:uppercase">{task?.member?.name }</span>
                                          </Table.Cell>
                                        );
                                      case "skill":
                                        return (
                                          <Table.Cell className="!px-6 !py-3" key={head.key}>
                                            {task.skill}
                                          </Table.Cell>
                                        );
                                      case "start":
                                        return (
                                          <Table.Cell className="!px-6 !py-3" key={head.key}>
                                            {task?.adj_start_date ? formatDate(task?.adj_start_date): ""}
                                          </Table.Cell>
                                        );
                                      case "completion":
                                        return (
                                          <Table.Cell className="!px-6 !py-3" key={head.key}>
                                            {task.completion}
                                          </Table.Cell>
                                        );
                                      default:
                                        return null;
                                    }
                                  })}
                                </Table.Row>
                            )
                          })
                        ) : (
                          <Table.Row>
                            <Table.Cell colSpan={tableHeadMap.filter(head => visibleColumns[head.key]).length} className="text-center py-10">
                              <div className="dark:text-white text-gray-800 flex justify-center items-center h-[50vh]">
                                {searchTerm ? (
                                  <div className="text-center">
                                    <p>No tasks found for your search: <span className="font-medium">{searchTerm}</span></p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Try adjusting your search criteria</p>
                                  </div>
                                ) : !sim_flag && hasSimulationTasks ? (
                                  <div className="text-center">
                                    <p>You currently have no task data in live mode.</p>
                                    <p className="font-semibold text-blue-600 dark:text-blue-400">Tasks are available in simulation mode. Please toggle to simulation mode to view them.</p>
                                  </div>
                                ) : !sim_flag ? (
                                  <div className="text-center">
                                    <p>You currently have no task data in live mode. Please create a new task to begin.</p>
                                  </div>
                                ) : (
                                  <div className="text-center">
                                    You currently have no task data in simulation mode. Please create a new task to begin.
                                  </div>
                                )}
                              </div>
                            </Table.Cell>
                          </Table.Row>
                        )}
                    </Table.Body>
                  </Table>
                </div>
              </div>
            </div>
          </div>
      </section>
    </div>
  );
};

export default TaskTable;